import { useEffect, useRef } from 'react';
import { PREF_KEYS, PREF_DEFAULTS } from '@maker-toolchest/shared';
import { useUserPreference, useSetUserPreference } from '@/features/preferences';
import { useUIStore } from './ui';
import { preferencesCache } from '@/lib/preferencesCache';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook to sync UI store with user preferences API
 * This should be called once at the app root level to initialize
 * and maintain sync between Zustand state and database preferences
 */
export function useUIPreferences() {
  const { user } = useAuth();
  const userId = user?.id;

  const { data: sidebarCollapsed, isLoading: sidebarLoading } = useUserPreference<boolean>(
    PREF_KEYS.UI.SIDEBAR_COLLAPSED,
    PREF_DEFAULTS.UI.SIDEBAR_COLLAPSED
  );
  const { data: collectionsFilterOpen, isLoading: filterLoading } = useUserPreference<boolean>(
    PREF_KEYS.UI.COLLECTIONS_FILTER_OPEN,
    PREF_DEFAULTS.UI.COLLECTIONS_FILTER_OPEN
  );
  const setUserPreference = useSetUserPreference();

  // Get stable references to store functions
  const setSidebarCollapsed = useUIStore((state) => state.setSidebarCollapsed);
  const setCollectionsFilterOpen = useUIStore((state) => state.setCollectionsFilterOpen);

  // Track if we've initialized from preferences to prevent saving during init
  const initializedRef = useRef({ sidebar: false, filter: false });

  // Debounce timers to prevent excessive API calls from rapid UI changes
  const debounceTimersRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Hydrate from cache immediately on mount
  useEffect(() => {
    if (!userId) return;

    const cachedSidebar = preferencesCache.get(userId, PREF_KEYS.UI.SIDEBAR_COLLAPSED);
    const cachedFilter = preferencesCache.get(userId, PREF_KEYS.UI.COLLECTIONS_FILTER_OPEN);

    if (cachedSidebar !== undefined) {
      setSidebarCollapsed(cachedSidebar);
      initializedRef.current.sidebar = true;
    }

    if (cachedFilter !== undefined) {
      setCollectionsFilterOpen(cachedFilter);
      initializedRef.current.filter = true;
    }
  }, [userId, setSidebarCollapsed, setCollectionsFilterOpen]);

  // Initialize Zustand from preferences when they load (only once)
  useEffect(() => {
    if (!sidebarLoading && sidebarCollapsed !== undefined && !initializedRef.current.sidebar) {
      setSidebarCollapsed(sidebarCollapsed);
      initializedRef.current.sidebar = true;
    }
  }, [sidebarCollapsed, sidebarLoading, setSidebarCollapsed]);

  useEffect(() => {
    if (!filterLoading && collectionsFilterOpen !== undefined && !initializedRef.current.filter) {
      setCollectionsFilterOpen(collectionsFilterOpen);
      initializedRef.current.filter = true;
    }
  }, [collectionsFilterOpen, filterLoading, setCollectionsFilterOpen]);

  // Subscribe to Zustand changes and save to preferences (only after initialization)
  // Debounced to prevent excessive API calls from rapid UI changes
  useEffect(() => {
    const DEBOUNCE_MS = 300;

    const debouncedSave = (key: string, value: any) => {
      // Clear existing timer for this key
      if (debounceTimersRef.current[key]) {
        clearTimeout(debounceTimersRef.current[key]);
      }

      // Set new timer
      debounceTimersRef.current[key] = setTimeout(() => {
        setUserPreference.mutate(
          { key, value },
          {
            onSuccess: () => {
              // Update cache only on successful API save
              if (userId) {
                preferencesCache.set(userId, key, value);
              }
            },
          }
        );
        delete debounceTimersRef.current[key];
      }, DEBOUNCE_MS);
    };

    const unsubscribe = useUIStore.subscribe((state, prevState) => {
      // Only save if initialized and value actually changed
      if (initializedRef.current.sidebar && state.sidebarCollapsed !== prevState.sidebarCollapsed) {
        debouncedSave(PREF_KEYS.UI.SIDEBAR_COLLAPSED, state.sidebarCollapsed);
      }

      // Only save if initialized and value actually changed
      if (initializedRef.current.filter && state.collectionsFilterOpen !== prevState.collectionsFilterOpen) {
        debouncedSave(PREF_KEYS.UI.COLLECTIONS_FILTER_OPEN, state.collectionsFilterOpen);
      }
    });

    return unsubscribe;
  }, [setUserPreference, userId]);

  return {
    isLoading: sidebarLoading || filterLoading,
  };
}
